import React, { useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { intakeRequest } from './clientIntakeApi';
import './clientIntake.css';

const EMPTY = { full_name: '', phone: '', email: '', optional_values: {} };

export default function SccClientDetailsIntake() {
  const tokenRef = useRef(window.location.hash.slice(1));
  const formRef = useRef(null);
  const [phase, setPhase] = useState('checking');
  const [values, setValues] = useState(EMPTY);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [optionalFields, setOptionalFields] = useState([]);
  const [expiresAt, setExpiresAt] = useState(null);

  useEffect(() => {
    document.title = 'Confirm Your Details · VISINVOKE SCC';
    let active = true;
    const controller = new AbortController();
    if (!/^[A-Za-z0-9_-]{43}$/.test(tokenRef.current)) { setPhase('unavailable'); return undefined; }
    intakeRequest('/open', { token: tokenRef.current }, { publicSession: true, signal: controller.signal })
      .then(result => { if (active) { setOptionalFields(result.optional_fields || []); setExpiresAt(result.expires_at); setPhase('ready'); } })
      .catch(e => { if (active) { setPhase('unavailable'); setError(e.status >= 500 ? 'Intake is temporarily unavailable. Reopen the QR to try again.' : ''); } });
    return () => { active = false; controller.abort(); };
  }, []);

  useEffect(() => {
    if (!expiresAt || phase !== 'ready') return undefined;
    const timer = setTimeout(() => { setValues(EMPTY); setRevealed(false); setPhase('unavailable'); }, Math.max(0, Date.parse(expiresAt) - Date.now()));
    return () => clearTimeout(timer);
  }, [expiresAt, phase]);

  useEffect(() => {
    if (!revealed) return undefined;
    const timer = setTimeout(() => setRevealed(false), 10000);
    return () => clearTimeout(timer);
  }, [revealed]);

  useEffect(() => {
    const hide = () => {
      setRevealed(false);
      if (document.visibilityState === 'hidden') { formRef.current?.reset(); setValues(EMPTY); }
    };
    const leave = () => { formRef.current?.reset(); setValues(EMPTY); setRevealed(false); };
    const restore = e => { if (e.persisted) { setValues(EMPTY); setRevealed(false); setPhase('unavailable'); } };
    document.addEventListener('visibilitychange', hide);
    window.addEventListener('pagehide', leave);
    window.addEventListener('pageshow', restore);
    return () => { document.removeEventListener('visibilitychange', hide); window.removeEventListener('pagehide', leave); window.removeEventListener('pageshow', restore); };
  }, []);

  const submit = async event => {
    event.preventDefault();
    if (busy) return;
    setBusy(true); setError(''); setRevealed(false);
    try {
      await intakeRequest('/complete', { token: tokenRef.current, ...values }, { publicSession: true });
      formRef.current?.reset();
      tokenRef.current = '';
      window.history.replaceState(null, '', '/scc/client-details');
      flushSync(() => { setValues(EMPTY); setPhase('completed'); setBusy(false); });
      // The data-free fallback is already rendered before asking the browser to close.
      try { window.close(); } catch { /* data-free completion remains visible */ }
    } catch (e) {
      if ([403, 404, 410].includes(e.status)) { setValues(EMPTY); setPhase('unavailable'); }
      else setError(e.message);
      setBusy(false);
    }
  };

  return <main className="scc-client-intake">
    <section className="scc-client-intake-card">
      <p className="scc-intake-brand">VISINVOKE · SERVICE COMMAND CENTRE</p>
      {phase === 'ready' ? <>
        <h1>Confirm Your Details</h1>
        <p>Enter these details so the technician receives them exactly as intended.</p>
        <form ref={formRef} onSubmit={submit} autoComplete="off">
          {[
            ['full_name', 'Full Name', 'text', 255],
            ['phone', 'Phone Number', 'tel', 64],
            ['email', 'Email Address', 'email', 255],
          ].map(([key, label, type, maximum]) => <label key={key} htmlFor={`intake-${key}`}>
            {label}
            <input id={`intake-${key}`} name={key} type={type} required maxLength={maximum}
              autoComplete="off" autoCapitalize={key === 'full_name' ? 'words' : 'none'}
              spellCheck={key === 'full_name'} value={values[key]} disabled={busy}
              onChange={e => setValues(previous => ({ ...previous, [key]: e.target.value }))} />
          </label>)}
          {optionalFields.map(field => <label key={field.id} htmlFor={`intake-${field.id}`}>
            {field.label} <span>(optional)</span>
            <input id={`intake-${field.id}`} name={field.id}
              type={field.type === 'password' && revealed ? 'text' : field.type} maxLength={field.maxLength}
              autoComplete="off" autoCapitalize="none" spellCheck={false} disabled={busy}
              value={values.optional_values[field.id] || ''}
              onChange={e => setValues(previous => ({ ...previous, optional_values: { ...previous.optional_values, [field.id]: e.target.value } }))} />
          </label>)}
          {optionalFields.some(field => field.type === 'password') && <>
          <button className="scc-intake-reveal" type="button" aria-pressed={revealed} disabled={busy}
            onClick={() => setRevealed(value => !value)}>{revealed ? 'Hide password / PIN' : 'Show password / PIN briefly'}</button>
          <p className="scc-intake-hint">Enter only the sign-in credential requested for this service.</p>
          </>}
          {error && <p role="alert">{error}</p>}
          <button type="submit" disabled={busy}>{busy ? 'Sending…' : 'Completed'}</button>
        </form>
      </> : <>
        <h1>{phase === 'completed' ? 'Completed' : phase === 'checking' ? 'Opening intake…' : 'Intake no longer available'}</h1>
        <p role="status">{phase === 'completed' ? 'Your details have been sent to the technician.' : phase === 'checking' ? 'Please wait a moment.' : error || 'This intake has been completed or is no longer available.'}</p>
      </>}
    </section>
  </main>;
}
